import React, { useEffect, useState, useRef } from "react";
import { useServiceRequest } from "../context/ServiceRequestContext";
import { updateFormData } from "../context/useFormDataUpdater";
import { sendNotification } from "../../../utils/NotificationHelper";
import { nowIST, todayIST, maxDateIST } from "../../../utils/dateutil";
import {
    Role,
    ScheduleState,
    Proposal,
    SlotStatus,
} from "./types";

import {
    now,
    addHour,
    generateTimeOptions,
    format,
} from "./utils";

import { ProposalColumn } from "./components/ProposalColumn";
import { RequestTrackingService } from "../../../api";

// ---------------- DEFAULT ----------------
const defaultState: ScheduleState = {
    version: 1,
    proposals: [],
    finalSlot: null,
    rescheduleCount: 0,
    isLocked: false,
    lastUpdatedAt: undefined,
};

const normalizeState = (data: any): ScheduleState => ({
    version: 1,
    proposals: data?.proposals ?? [],
    finalSlot: data?.finalSlot ?? null,
    rescheduleCount: data?.rescheduleCount ?? 0,
    isLocked: data?.isLocked ?? false,
    lastUpdatedAt: data?.lastUpdatedAt,
});

const Scheduler = ({ role, onRefresh }: { role: Role; onRefresh?: () => void }) => {
    const { details } = useServiceRequest();
    const requestId = String(details?.id);

    const [state, setState] = useState<ScheduleState>(defaultState);
    const [loading, setLoading] = useState(false);

    const [slot1, setSlot1] = useState({ date: "", start: "", end: "" });
    const [slot2, setSlot2] = useState({ date: "", start: "", end: "" });

    const prevRef = useRef<ScheduleState>(defaultState);
    const timeOptions = generateTimeOptions();

    // ---------------- PERSIST ----------------
    const persist = async (next: ScheduleState) => {
        try {
            setLoading(true);

            await updateFormData(requestId, "Scheduling", next);

            setState(next);
            prevRef.current = next;
        } catch (err) {
            console.error(err);
            alert("Save failed");
        } finally {
            setLoading(false);
        }
    };

    const track = async (action: "PROPOSE" | "ACCEPT" | "DECLINE") => {
        try {
            await RequestTrackingService.createStatusServiceRequestsStatusPost({
                service_request_id: Number(requestId),
                title: `Scheduling ${action}`,
                notes: `Scheduling ${action} by ${role}`,
                current_sub_status_id: null,
                new_sub_status_id: 5, // TODO: map properly
                action_key: action,
                actor_role: role.toUpperCase(),
            });
        } catch (err) {
            console.error("Tracking failed", err);
        }
    };

    const notify = async (
        action: "PROPOSE" | "ACCEPT" | "DECLINE",
        actorRole: Role,
        proposal?: Proposal,
        selectedSlot?: { date: string; start: string; end: string }
    ) => {
        try {
            // 🎯 Template mapping
            const templateMap: Record<string, string> = {
                PROPOSE_User: "SCHEDULING_PROPOSED_BY_USER",
                PROPOSE_Admin: "SCHEDULING_PROPOSED_BY_ADMIN",
                ACCEPT_User: "SCHEDULING_ACCEPTED_BY_USER",
                ACCEPT_Admin: "SCHEDULING_ACCEPTED_BY_ADMIN",
                DECLINE_User: "SCHEDULING_DECLINED_BY_USER",
                DECLINE_Admin: "SCHEDULING_DECLINED_BY_ADMIN",
            };

            const templateName = templateMap[`${action}_${actorRole}`];
            if (!templateName) return;

            // 🎯 Decide recipient (send to opposite role)
            const recipient =
                actorRole === "User"
                    ? {
                        name: details?.admin_name,
                        email: details?.admin_email,
                    }
                    : {
                        name: details?.requestor_name,
                        email: details?.requestor_email,
                    };

            if (!recipient?.email) return;

            // 🎯 Build slot info (for email templates)
            let slotInfo = "";

            if (action === "PROPOSE" && proposal) {
                slotInfo = proposal.slots
                    .map(
                        (s) =>
                            `${s.slot.date} ${s.slot.start}-${s.slot.end} (IST)`
                    )
                    .join(", ");
            }

            if (action === "ACCEPT" && selectedSlot) {
                slotInfo = `${selectedSlot.date} ${selectedSlot.start}-${selectedSlot.end} (IST)`;
            }

            // 🎯 Extra context for template
            const extraContext: Record<string, string> = {
                ActorRole: actorRole,
                Action: action,
                SlotDetails: slotInfo,
            };

            await sendNotification(
                templateName,
                requestId,
                recipient,
                "", // CC if needed
                "Interview Scheduling",
                extraContext
            );
        } catch (err) {
            console.error("Notification failed", err);
        }
    };

    const applyExpiry = (proposals: Proposal[]): Proposal[] => {
        const nowDate = new Date();

        return proposals.map(p => ({
            ...p,
            slots: p.slots.map(s => {
                if (s.status !== "pending") return s;

                const endTime = new Date(`${s.slot.date}T${s.slot.end}:00`);

                if (endTime < nowDate) {
                    return { ...s, status: "expired" as SlotStatus };
                }

                return s;
            }),
        }));
    };

    const getRoleProposals = (role: Role) =>
        state.proposals.filter(p => p.proposedBy === role);

    const allProposalsRejected = () =>
        state.proposals.length > 0 &&
        state.proposals.every(p =>
            p.slots.every(s => s.status === "declined")
        );

    const hasPendingFromMe = () =>
        state.proposals.some(
            (p) =>
                p.proposedBy === role &&
                p.slots.some((s) => s.status === "pending")
        );

    const getRoleIteration = (role: Role) =>
        getRoleProposals(role).length + 1;

    const MAX_PER_ROLE = 2;

    const hasReachedLimit = () => {
        const userCount = getRoleProposals("User").length;
        const adminCount = getRoleProposals("Admin").length;

        return userCount >= MAX_PER_ROLE && adminCount >= MAX_PER_ROLE;
    };
    // ---------------- HYDRATE ----------------
    useEffect(() => {
        const incoming = details?.form_data?.Scheduling;
        const normalized = incoming ? normalizeState(incoming) : defaultState;

        setState({
            ...normalized,
            proposals: applyExpiry(normalized.proposals),
        });
    }, [details]);

    const latest = state.proposals?.[state.proposals.length - 1];

    const limitReached = hasReachedLimit() && allProposalsRejected();

    // ---------------- ACTIONS ----------------
    const handlePropose = async () => {
        // 🚫 Block if my previous proposal is still pending
        if (hasPendingFromMe()) {
            return alert("Your previous proposal is still pending review.");
        }

        // ✅ Collect only valid slots (slot2 optional)
        const slotsInput = [slot1, slot2].filter(
            (s) => s.date && s.start
        );

        // 🚫 At least one slot required
        if (slotsInput.length === 0) {
            return alert("Please select at least one slot");
        }

        // 🚫 Duplicate slot validation
        const slotKeys = slotsInput.map(
            (s) => `${s.date.trim()}_${s.start.trim()}`
        );

        if (new Set(slotKeys).size !== slotKeys.length) {
            return alert("Both slots cannot have the same date and time");
        }

        // 🚫 Limit reached (both roles exhausted AND all rejected)
        if (limitReached) {
            return alert(
                "Schedule negotiation limit reached. Please settle thru phone conversation."
            );
        }

        // 🚫 Per-role limit
        if (getRoleProposals(role).length >= MAX_PER_ROLE) {
            return alert("You have reached maximum proposal limit.");
        }

        // 🚫 Date range validation (extra safety beyond UI)
        const maxDate = maxDateIST(15);
        const today = todayIST();

        const invalidDate = slotsInput.some(
            (s) => s.date < today || s.date > maxDate
        );

        if (invalidDate) {
            return alert("Slots must be within the next 15 days.");
        }

        // 🚫 Past time validation (for today)
        const now = nowIST();

        const invalidTime = slotsInput.some((s) => {
            if (s.date !== today) return false;

            const slotDateTime = new Date(`${s.date}T${s.start}:00+05:30`);
            return slotDateTime < now;
        });

        if (invalidTime) {
            return alert("Cannot select past time slots.");
        }

        // ✅ Iteration calculation
        const iteration = getRoleIteration(role);

        // ✅ Build proposal slots
        const slots = slotsInput.map((s) => ({
            slot: { ...s, end: addHour(s.start) },
            status: "pending" as SlotStatus,
        }));

        const proposal: Proposal = {
            id: crypto.randomUUID(),
            proposedBy: role,
            iteration,
            slots,
            createdAt: nowIST(),
        };

        // Auto-decline ALL existing pending slots
        const updated = state.proposals.map((p) => ({
            ...p,
            slots: p.slots.map((s) =>
                s.status === "pending"
                    ? { ...s, status: "declined" as SlotStatus }
                    : s
            ),
        }));

        const next: ScheduleState = {
            ...state,
            proposals: [...updated, proposal],
        };

        // Persist + Track
        await persist(next);
        await track("PROPOSE");
        await notify("PROPOSE", role, proposal);
        onRefresh?.();

        // Reset form
        setSlot1({ date: "", start: "", end: "" });
        setSlot2({ date: "", start: "", end: "" });
    };

    const handleAccept = async (proposalId: string, index: number) => {
        const updated = state.proposals.map((p) => {
            if (p.id === proposalId) {
                return {
                    ...p,
                    slots: p.slots.map((s, i) => ({
                        ...s,
                        status: (i === index ? "accepted" : "declined") as SlotStatus,
                    })),
                };
            }
            return p;
        });

        const selected = updated.find(p => p.id === proposalId)!.slots[index].slot;

        const next: ScheduleState = {
            ...state,
            proposals: updated,
            finalSlot: selected,
            isLocked: true,
        };

        await persist(next);
        await track("ACCEPT");
        await notify("ACCEPT", role, undefined, selected);
        onRefresh?.();

    };

    const handleDecline = async (proposalId: string, index: number) => {
        const updated = state.proposals.map((p) => {
            if (p.id === proposalId) {
                return {
                    ...p,
                    slots: p.slots.map((s, i) => ({
                        ...s,
                        status: (i === index ? "declined" : s.status) as SlotStatus,
                    })),
                };
            }
            return p;
        });

        const next: ScheduleState = {
            ...state,
            proposals: updated,
        };

        await persist(next);
        await track("DECLINE");
        await notify("DECLINE", role);
        onRefresh?.();
    };

    // ---------------- UI ----------------
    return (
        <div className="p-4 space-y-4">
            {/* {hasPendingFromMe() && (
                <div className="text-yellow-600 text-sm">
                    Your proposal is awaiting response.
                </div>
            )} */}
            {limitReached && (
                <div className="bg-red-100 text-red-700 p-3 rounded">
                    Schedule negotiation limit reached. Please settle thru phone conversation.
                </div>
            )}
            {/* FINAL */}
            {state.finalSlot && (
                <div className="bg-green-100 p-3 rounded flex justify-between">
                    <div><strong>Final:</strong> {format(state.finalSlot)}</div>
                </div>
            )}

            {/* BOARD */}
            <div className="flex gap-4">
                <ProposalColumn
                    title="User Proposals"
                    proposals={state.proposals.filter(p => p.proposedBy === "User")}
                    role={role}
                    onAccept={handleAccept}
                    onDecline={handleDecline}
                />
                <ProposalColumn
                    title="Coordinator Proposals"
                    proposals={state.proposals.filter(p => p.proposedBy === "Admin")}
                    role={role}
                    onAccept={handleAccept}
                    onDecline={handleDecline}
                />
            </div>

            {/* FORM (always visible unless locked) */}
            {/* {!state.isLocked && !limitReached && (
                <div className="border p-3 rounded space-y-2">
                    {[slot1, slot2].map((s, i) => (
                        <div key={i} className="flex gap-2">
                            <input
                                type="date"
                                value={s.date}
                                min={todayIST()}
                                max={maxDateIST(15)}
                                onChange={(e) =>
                                    i === 0
                                        ? setSlot1({ ...s, date: e.target.value })
                                        : setSlot2({ ...s, date: e.target.value })
                                }
                                className="border px-2 py-1 rounded"
                            />

                            <select
                                value={s.start}
                                onChange={(e) =>
                                    i === 0
                                        ? setSlot1({ ...s, start: e.target.value })
                                        : setSlot2({ ...s, start: e.target.value })
                                }
                                className="border px-2 py-1 rounded"
                            >
                                <option value="" className="text-xs">Time (IST)</option>
                                {timeOptions.map((t) => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>
                    ))}

                    <button
                        onClick={handlePropose}
                        disabled={loading || hasPendingFromMe() || limitReached}
                        className="bg-green-600 text-white px-3 py-1 rounded disabled:opacity-50"
                    >
                        {loading ? "Saving..." : "Propose"}
                    </button>
                </div>
            )} */}

            {/* FORM (visible unless locked or awaiting response) */}
            {!state.isLocked && !limitReached && (
                <>
                    {hasPendingFromMe() ? (
                        <div className="border p-3 rounded space-y-2 text-yellow-600 text-sm">
                            Your proposal is awaiting response.
                        </div>
                    ) : (
                        <div className="border p-3 rounded space-y-2">
                            {[slot1, slot2].map((s, i) => (
                                <div key={i} className="flex gap-2">
                                    <input
                                        type="date"
                                        value={s.date}
                                        min={todayIST()}
                                        max={maxDateIST(15)}
                                        onChange={(e) =>
                                            i === 0
                                                ? setSlot1({ ...s, date: e.target.value })
                                                : setSlot2({ ...s, date: e.target.value })
                                        }
                                        className="border px-2 py-1 rounded"
                                    />

                                    <select
                                        value={s.start}
                                        onChange={(e) =>
                                            i === 0
                                                ? setSlot1({ ...s, start: e.target.value })
                                                : setSlot2({ ...s, start: e.target.value })
                                        }
                                        className="border px-2 py-1 rounded"
                                    >
                                        <option value="" className="text-xs">Time (IST)</option>
                                        {timeOptions.map((t) => (
                                            <option key={t} value={t}>{t}</option>
                                        ))}
                                    </select>
                                </div>
                            ))}

                            <button
                                onClick={handlePropose}
                                disabled={loading}
                                className="bg-green-600 text-white px-3 py-1 rounded disabled:opacity-50"
                            >
                                {loading ? "Saving..." : "Propose"}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Scheduler;